import React, { useState, useEffect } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import moment from "moment/moment";
import "../pagination/pagination.css";
import Pagination from "../pagination/pagination";
import Select from "react-select";
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import LeaseApiService from "../../services/apiService";
import { ClipLoader } from 'react-spinners';
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import "jspdf-autotable";

const MONTHS = moment.months().map((label, i) => ({ value: i + 1, label }));
const DATE_FORMAT = "DD-MMM-YYYY";
const currentMonth = () => MONTHS[moment().month()];

const formatDate = (value) => (value ? moment(value).format(DATE_FORMAT) : "");

// One column list drives the grid, the PDF and the Excel export.
const COLUMNS = [
    { header: "Cust Type", field: "custType", width: 110 },
    { header: "Customer No", field: "customerNo", width: 120 },
    { header: "Customer Name", field: "customerName", width: 200 },
    { header: "Property", field: "properTyName", width: 180 },
    { header: "Unit No", field: "unitNo", width: 100 },
    { header: "Birth Date", field: "birthDate", width: 130, isDate: true },
    { header: "Birth Day", field: "birthDAY", width: 130, isDate: true },
    { header: "Mobile No", field: "mobileNo", width: 130 },
    { header: "Email", field: "emailID", width: 220 },
    { header: "Sales Person", field: "salesPerson", width: 150 },
];

// Birthday order: the day within the year the birthday falls (birthDAY), then the original birth date.
const byBirthday = (rows) => [...rows].sort((a, b) =>
    moment(a.birthDAY).valueOf() - moment(b.birthDAY).valueOf() || moment(a.birthDate).valueOf() - moment(b.birthDate).valueOf());

const cellValue = (row, col) => (col.isDate ? formatDate(row?.[col.field]) : row?.[col.field] ?? "");

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#F5F5F5" : null,
        color: "#000",
        ":hover": { backgroundColor: "#F5F5F5", color: "#000" },
    }),
};

const CustomerBirthdayList = () => {
    const apiService = new LeaseApiService();
    const [rowData, setRowData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState();
    const [FilterTab, setFilterTab] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [formData, setFormData] = useState({ property: null, month: currentMonth() });

    useEffect(() => {
        (async () => {
            try {
                const params = new URLSearchParams();
                params.append('dropdownTypes', 'LRAgreeSearchProperties');
                const res = await apiService.getApiParamater('REPORT_MULTI_DROP', `?${params.toString()}`);
                setPropertyList(res?.data?.dropdowns?.LRAgreeSearchProperties || []);
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
            }
        })();
    }, []);

    const buildParams = (pageNo, size) => {
        const params = new URLSearchParams();
        params.append("PageNo", pageNo || 1);
        params.append("PageSize", size || 20);
        if (formData.property?.label) params.append("PropertyName", formData.property.label);
        if (formData.month?.value) params.append("BirthMonth", formData.month.value);
        return params;
    };

    const getItems = async (newPageNo = pagination?.currentPage) => {
        try {
            setLoader(true);
            const res = await apiService.getApiParamater("REPORT_CUSTOMER_BIRTHDAY", `?${buildParams(newPageNo, pageSize).toString()}`);
            if (res?.data?.result) {
                setRowData(byBirthday(res.data.result));
                setPagination(res.data.paginationData);
            } else {
                setRowData([]);
                setPagination(undefined);
                if (res?.data?.message) toast.error(res.data.message);
            }
        } catch (error) {
            console.error("Error fetching items:", error);
            toast.error("Unable to load the report.");
        } finally {
            setLoader(false);
        }
    };

    // Exports cover every page, not just the one on screen.
    const getAllItems = async () => {
        const total = pagination?.totalRecords;
        if (!total) return [];
        try {
            const res = await apiService.getApiParamater("REPORT_CUSTOMER_BIRTHDAY", `?${buildParams(1, total).toString()}`);
            return byBirthday(res?.data?.result || []);
        } catch (error) {
            console.error("Error fetching export data:", error);
            toast.error("Unable to export the report.");
            return [];
        }
    };

    const handleClearFilters = () => {
        setFormData({ property: null, month: currentMonth() });
        setRowData([]);
        setPagination(undefined);
    };

    const exportPDF = async () => {
        const data = await getAllItems();
        if (!data.length) return;
        const doc = new jsPDF("landscape", "pt", "A4");
        doc.setFillColor(110, 81, 153);
        doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");
        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text("Customer Birthday List", 40, 32);
        doc.setFontSize(9);
        doc.text(`Generated: ${moment().format(DATE_FORMAT)}`, doc.internal.pageSize.width - 140, 32);
        doc.setTextColor(0);
        doc.autoTable({
            startY: 60,
            head: [COLUMNS.map((c) => c.header)],
            body: data.map((row) => COLUMNS.map((c) => cellValue(row, c))),
            styles: { fontSize: 8, cellPadding: 5, valign: "middle" },
            headStyles: { fillColor: [110, 81, 153], textColor: 255, fontStyle: "bold", halign: "center" },
            bodyStyles: { halign: "center" },
            alternateRowStyles: { fillColor: [240, 237, 245] },
            didDrawPage: (d) => {
                doc.setFontSize(8);
                doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: "center" });
            },
        });
        doc.save("Customer_Birthday_List.pdf");
    };

    const exportExcel = async () => {
        const data = await getAllItems();
        if (!data.length) return;
        const dataRows = data.map((row) => COLUMNS.map((c) => cellValue(row, c)));
        const worksheet = XLSX.utils.aoa_to_sheet([
            ["CUSTOMER BIRTHDAY LIST"],
            COLUMNS.map((c) => c.header),
            ...dataRows,
        ]);
        worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMNS.length - 1 } }];
        worksheet["!cols"] = COLUMNS.map((c, i) => ({
            wch: Math.min(Math.max(c.header.length, ...dataRows.map((r) => String(r[i] ?? "").length)) + 3, 40),
        }));
        worksheet["!rows"] = [{ hpt: 28 }, { hpt: 30 }];

        const border = {
            top: { style: "thin", color: { rgb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { rgb: "FFD9D9D9" } },
            left: { style: "thin", color: { rgb: "FFD9D9D9" } },
            right: { style: "thin", color: { rgb: "FFD9D9D9" } },
        };
        if (worksheet["A1"]) {
            worksheet["A1"].s = {
                font: { bold: true, sz: 16, color: { rgb: "FFFFFFFF" } },
                fill: { fgColor: { rgb: "FF6E5199" } },
                alignment: { horizontal: "left", vertical: "center" },
                border,
            };
        }
        COLUMNS.forEach((_, c) => {
            const head = worksheet[XLSX.utils.encode_cell({ r: 1, c })];
            if (head) {
                head.s = {
                    font: { bold: true, sz: 10, color: { rgb: "FFFFFFFF" } },
                    fill: { fgColor: { rgb: "FF666666" } },
                    alignment: { horizontal: "center", vertical: "center", wrapText: true },
                    border,
                };
            }
            dataRows.forEach((_, r) => {
                const cell = worksheet[XLSX.utils.encode_cell({ r: r + 2, c })];
                if (cell) cell.s = { font: { sz: 10 }, alignment: { vertical: "center", horizontal: "left" }, border };
            });
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Birthday List");
        XLSX.writeFile(workbook, "Customer_Birthday_List.xlsx");
    };

    const columnDefs = COLUMNS.map((c) => ({
        headerName: c.header,
        field: c.field,
        width: c.width,
        filter: true,
        floatingFilter: true,
        suppressMovable: true,
        ...(c.isDate ? { valueFormatter: (p) => formatDate(p.value) } : {}),
    }));

    return (
        <div className="p-3">
            <div className="cost-center-section">
                <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                    <div className="h6 pt-1">Search Parameters</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain">
                            <div className="d-flex">
                                <label className="form-label">Property</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    placeholder="Select..."
                                    styles={selectStyles}
                                    value={formData.property}
                                    onChange={(option) => setFormData((prev) => ({ ...prev, property: option }))}
                                    options={propertyList
                                        .slice()
                                        .sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' }))
                                        .map((p) => ({ value: p.value, label: p.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Birth Month</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                    menuPortalTarget={document.body}
                                    placeholder="Select..."
                                    styles={selectStyles}
                                    value={formData.month}
                                    onChange={(option) => setFormData((prev) => ({ ...prev, month: option }))}
                                    options={MONTHS}
                                />
                            </div>
                            <div className="d-flex gap-1">
                                {loader ? (
                                    <div className="mainbtnGry flex-end gap-2 w-50">
                                        <ClipLoader color="#ffffff" className="mt-1" size={25} />
                                    </div>
                                ) : (
                                    <div onClick={() => getItems(1)} className="mainbtnGry flex-end gap-2 w-50">
                                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                                        <div className="center">Search</div>
                                    </div>
                                )}
                                <div onClick={handleClearFilters} className="mainbtnRed flex-end gap-2 w-50">
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">Reset</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="border-box2 p-3">
                <div className="d-flex justify-contant-between">
                    <h5 className="col mainInvHead">Customer Birthday List</h5>
                    <button className="btn btn-primary" onClick={exportPDF} disabled={!rowData.length}>
                        Export as PDF
                    </button>
                    <button className="btn btn-secondary mx-3" onClick={exportExcel} disabled={!rowData.length}>
                        Export as Excel
                    </button>
                </div>
                <div className="gridshad mt-3">
                    <div className="ag-theme-quartz" style={{ height: 650 }}>
                        <AgGridReact
                            getRowStyle={(params) => ({ background: params.node.rowIndex % 2 === 0 ? "#F0EDF5" : "#fff" })}
                            rowData={rowData}
                            enableFilter={true}
                            floatingFilter={true}
                            columnDefs={columnDefs}
                        />
                    </div>
                    <Pagination
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        pagination={pagination}
                        getItems={getItems}
                    />
                </div>
            </div>
        </div>
    );
};
export default CustomerBirthdayList;
